import { Link } from "react-router-dom";
import { Trophy, Calendar, Crown, ChevronRight, Globe, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";

interface UpcomingEventsSpotlightProps {
  showUpgradePrompt: boolean;
  onUpgrade: () => void;
}

const UPCOMING_EVENTS = [
  {
    id: "chl-final",
    title: "CHL Final – Frölunda 3-2 Luleå (OT)",
    icon: "🏒",
    startDate: new Date("2026-03-03"),
    location: "Scandinavium, Gothenburg",
  },
  {
    id: "shl-playoffs",
    title: "SHL Playoffs 2026",
    icon: "🇸🇪",
    startDate: new Date("2026-03-10"),
    location: "Sweden",
  },
  {
    id: "khl-playoffs",
    title: "KHL Gagarin Cup",
    icon: "🏆",
    startDate: new Date("2026-03-01"),
    location: "Russia & Europe",
  },
  {
    id: "nhl-playoffs",
    title: "NHL Stanley Cup Playoffs",
    icon: "🇺🇸",
    startDate: new Date("2026-04-18"),
    location: "North America",
  },
  {
    id: "iihf-worlds",
    title: "IIHF World Championship",
    icon: "🌍",
    startDate: new Date("2026-05-15"),
    location: "Zürich & Fribourg, Switzerland",
  },
];

export default function UpcomingEventsSpotlight({ showUpgradePrompt, onUpgrade }: UpcomingEventsSpotlightProps) {
  // Sort by start date, show next 3
  const nextEvents = UPCOMING_EVENTS
    .filter(e => e.startDate > new Date())
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
    .slice(0, 3);

  return (
    <section className="mb-10">
      {/* Upgrade Teaser for Free Users */}
      {showUpgradePrompt && (
        <Card className="mb-6 border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-500/20 rounded-xl">
                  <Globe className="w-8 h-8 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-foreground flex items-center gap-2">
                    Choose Your Favorite Nation
                    <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-400">Pro/Elite</Badge>
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Get a personalized dashboard with your country's games, players, and alerts.
                  </p>
                </div>
              </div>
              <Button onClick={onUpgrade} className="gap-2 bg-amber-500 hover:bg-amber-600 text-black font-semibold">
                <Crown className="w-4 h-4" />
                Set Favorite Nation
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upcoming Events Card */}
      <Card className="relative overflow-hidden border-primary/30 bg-gradient-to-br from-primary/10 via-transparent to-transparent">
        <div className="absolute top-0 right-0 w-48 h-48 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        
        <CardContent className="relative p-6 md:p-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-6 h-6 text-primary" />
                <Badge variant="outline" className="text-xs border-primary/50 text-primary">Coming Up</Badge>
              </div>
              
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-2">
                What's Next in Hockey
              </h2>
              <p className="text-muted-foreground mb-4">
                Playoffs and world championships are around the corner!
              </p>

              <div className="flex flex-wrap items-center gap-3">
                <Link to="/#big-events">
                  <Button className="gap-2">
                    <Calendar className="w-4 h-4" />
                    View All Events
                  </Button>
                </Link>
                <Link to="/olympics-2026">
                  <Button variant="outline" className="gap-2">
                    <Trophy className="w-4 h-4" />
                    Olympics 2026 Results
                  </Button>
                </Link>
              </div>
            </div>

            {/* Next Events Preview */}
            <div className="lg:w-80">
              <h3 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3 flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-primary" />
                Coming Up
              </h3>
              <div className="space-y-2">
                {nextEvents.map((event) => (
                  <div key={event.id} className="bg-secondary/50 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{event.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{event.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(event.startDate, "MMM d, yyyy")} · {event.location}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Links */}
      <div className="mt-4 flex flex-wrap gap-2">
        <Link to="/#international-tournaments">
          <Badge variant="secondary" className="gap-1 hover:bg-secondary/80 cursor-pointer">
            <Trophy className="w-3 h-3" />
            World Championship
            <ChevronRight className="w-3 h-3" />
          </Badge>
        </Link>
        <Link to="/#live-games">
          <Badge variant="secondary" className="gap-1 hover:bg-secondary/80 cursor-pointer">
            Live Games
            <ChevronRight className="w-3 h-3" />
          </Badge>
        </Link>
        <Link to="/schedule">
          <Badge variant="secondary" className="gap-1 hover:bg-secondary/80 cursor-pointer">
            <Calendar className="w-3 h-3" />
            Full Schedule
            <ChevronRight className="w-3 h-3" />
          </Badge>
        </Link>
      </div>
    </section>
  );
}
